import React, { useEffect, useRef, useState } from "react";
import LoadingFeedback, { useLoadingState } from "../../components/loadingFeedback";
import BreadCrumbComp from "../../components/breadCrumb";
import HeaderBanner from "../../components/headerBanner";
import {
  engineerReportState,
  engineerReportTypeService,
  headerBannerType
} from "../../constants/enums";
import { Alert, CircularProgress, Divider, Grid } from "@mui/material";
import LaddaButton from "../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormCalendar from "../../components/calendar/formCalendar";
import InstitutionSection from "./engineeringReport/institutionSection";
import MedicalTechnologySection from "./engineeringReport/MedicalTechnologySection";
import ServiceTypeSection from "./engineeringReport/serviceTypeSection";
import CharacterCounter from "../../components/characterCounter";
import FormTextField from "../../components/textField/formTextField";
import SignSection from "./engineeringReport/signSection";
import ActivityEnginnerSection from "./engineeringReport/activityEnginnerSection";
import { useDispatch, useSelector } from "react-redux";
import {
  callActionApiAsync,
  callApiAsync,
  getEngineeringActById,
  getNonBusinessDay,
  getSelectsServiceOrder,
  getServiceOrderById,
  saveEngineeringAct
} from "../../services/apiService";
import SparePartsUsedSection from "./engineeringReport/sparePartsUsedSection";
import EvidencesSection from "./engineeringReport/evidencesSection.js";
import ToastNotification from "../../components/toastNotification/index.js";
import { formatDateOnly } from "../../services/dateService.js";
import VerificationModal from "../../components/modals/verificationModal/index.js";
import { setEngineeringReportId } from "../../redux/slices/serviceOrderSlice.js";
import { useNavigate, useLocation } from "react-router-dom";
import { hasAnyRoleUtil } from "../../utils/AuthorizedUtils/360Report";
import { BIOMEDICAL_ENGINEERING_REPORT_EDIT_ROLES } from "../../constants/biomedicalRoles";
import useUnsavedReportChanges from "../../services/useUnsavedReportChanges";

const CreateEngineeringReport = () => {
  const location = useLocation();
  const isNewReport = location.state?.isNew;

  const [serviceOrder, setServiceOrder] = useState({});
  const [brandData, setBrandData] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [loadingDetails, setLoadingDetails] = useLoadingState(true);

  const [activityData, setActivityData] = useState([]);
  const [disabledDates, setDisabledDates] = useState([]);
  const [ImageCount, setImageCount] = useState(0);

  const [openVerification, setOpenVerification] = useState(false);
  const [openEnableEditVerification, setOpenEnableEditVerification] =
    useState(false);
  const [hasSpareParts, setHasSpareParts] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signatureChanged, setSignatureChanged] = useState(false);
  const [openPartialSignatureWarning, setOpenPartialSignatureWarning] = useState(false);
  const [pendingEvidences, setPendingEvidences] = useState(false);
  const [pendingActivity, setPendingActivity] = useState(false);
  const [pendingSparePart, setPendingSparePart] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const protectedActionRef = useRef(null);
  const [activeAction, setActiveAction] = useState(null);

  const startProtectedAction = (action) => {
    if (protectedActionRef.current !== null) return false;
    protectedActionRef.current = action;
    setActiveAction(action);
    return true;
  };

  const endProtectedAction = () => {
    protectedActionRef.current = null;
    setActiveAction(null);
  };

  const openReportConfirmation = () => {
    if (!startProtectedAction("confirming")) return;
    setOpenVerification(true);
  };

  const closeReportConfirmation = () => {
    if (protectedActionRef.current !== "confirming") return;
    setOpenVerification(false);
    endProtectedAction();
  };

  const openUnsignedEditConfirmation = () => {
    if (!canEnableUnsignedEdit || !startProtectedAction("confirmUnsignedEdit")) return;
    setOpenEnableEditVerification(true);
  };

  const closeUnsignedEditConfirmation = () => {
    if (protectedActionRef.current !== "confirmUnsignedEdit") return;
    setOpenEnableEditVerification(false);
    endProtectedAction();
  };

  const enableUnsignedEdit = () => {
    if (protectedActionRef.current !== "confirmUnsignedEdit") return;
    protectedActionRef.current = "enableUnsignedEdit";
    setActiveAction("enableUnsignedEdit");
    if (!canEnableUnsignedEdit) {
      setToastConfig({
        severity: "warning",
        summary: "Acción no permitida",
        detail: "No puedes habilitar la edición de este reporte."
      });
      setShowToast(true);
      setOpenEnableEditVerification(false);
      endProtectedAction();
      return;
    }
    setIsEditingUnsignedReport(true);
    setOpenEnableEditVerification(false);
  };

  const runReportSave = async (state) => {
    const action = state === engineerReportState.partialSave ? "partial" : "report";
    if (action === "report" && protectedActionRef.current === "confirming") {
      protectedActionRef.current = action;
      setActiveAction(action);
    } else if (!startProtectedAction(action)) {
      return;
    }
    setSavingReport(true);
    try {
      await handleSaveAll(state);
    } finally {
      setSavingReport(false);
      if (protectedActionRef.current !== "navigating") {
        if (action === "report" && openVerification) {
          protectedActionRef.current = "confirming";
          setActiveAction("confirming");
        } else {
          endProtectedAction();
        }
      }
    }
  };
  const [isEditingUnsignedReport, setIsEditingUnsignedReport] =
    useState(false);

  const [showToast, setShowToast] = useState(false);

  const [isNew, setIsNew] = useState(false);

  const [toastConfig, setToastConfig] = useState({
    severity: "",
    summary: "",
    detail: ""
  });

  const [engineeringReport, setEngineeringReport] = useState(null);
  const [engineeringReportbyOrderId, setEngineeringReportbyOrderId] =
    useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const validationSchema = {};

  const today = new Date();
  const minDate = today;
  const maxDate = new Date(today.getFullYear(), 11, 31);

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields },
    reset,
    getFieldState,
    getValues,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onSubmit"
  });

  const userData = useSelector((state) => state.userData.userData);
  const canEditEngineeringReport = hasAnyRoleUtil(
    BIOMEDICAL_ENGINEERING_REPORT_EDIT_ROLES,
    userData.roleList
  );

  const serviceOrderId = useSelector(
    (state) => state.serviceOrder.serviceOrderId
  );
  const engineeringReportId = useSelector(
    (state) => state.serviceOrder.engineeringReportId
  );

  const loadData = () => {
    if (!engineeringReport) return;

    const hasValue = (value) =>
      value !== null && value !== undefined && value !== "";

    const fieldsToSet = [
      { name: "creationDate", value: engineeringReport.service_creation_date },
      { name: "closeDate", value: engineeringReport.service_closure_date },
      {
        name: "reportbyInstitution",
        value: engineeringReport.reported_novelty
      },
      { name: "engineeringReport", value: engineeringReport.report },
      { name: "observations", value: engineeringReport.observations },
      { name: "receivedByName", value: engineeringReport.received_by_name },
      {
        name: "receivedByPosition",
        value: engineeringReport.received_by_position
      },
      { name: "otherOp", value: engineeringReport.is_other_value }
    ];

    fieldsToSet.forEach(({ name, value }) => {
      if (hasValue(value) && !getFieldState(name).isDirty) {
        setValue(name, name.includes("Date") ? new Date(value) : value);
      }
    });

    const services = [];
    if (!isNew) {
    if (engineeringReport.is_corrective_maintenance)
        services.push(engineerReportTypeService.CorrectiveMaintenance);
      if (engineeringReport.is_preventive_maintenance)
        services.push(engineerReportTypeService.PreventiveMaintenance);
      if (engineeringReport.is_consumable_adjustments)
        services.push(engineerReportTypeService.ConsumablesAdjustment);
      if (engineeringReport.is_academic_activity)
        services.push(engineerReportTypeService.AcademicActivity);
      if (engineeringReport.is_preinstallation)
        services.push(engineerReportTypeService.PreInstallation);
      if (engineeringReport.is_installation)
        services.push(engineerReportTypeService.Installation);
      if (engineeringReport.is_software_update)
        services.push(engineerReportTypeService.SoftwareUpdate);
      if (engineeringReport.is_training)
        services.push(engineerReportTypeService.Training);
      if (engineeringReport.is_other)
        services.push(engineerReportTypeService.Other);
  
      setValue("services", services);
    }
    // Fechas adicionales
    if (hasValue(engineeringReport.preventive_maintenance_date)) {
      setValue(
        "preventive_maintenance_performed",
        new Date(engineeringReport.preventive_maintenance_date)
      );
    }
    if (hasValue(engineeringReport.next_maintenance_date)) {
      setValue(
        "nextMaintenance",
        new Date(engineeringReport.next_maintenance_date)
      );
    }
  };

  const getNonBusinessDaysData = async () => {
    await callApiAsync(
      () => getNonBusinessDay(),
      (data) => {
        setDisabledDates(data);
      },
      setLoadingDetails
    );
  };

  const getEngineeringActData = async () => {
    await callApiAsync(
      () => getEngineeringActById(engineeringReportId),
      (data) => {
        setEngineeringReport(data);
      },
      setLoadingDetails
    );
  };

  const getServiceOrderData = async () => {
    await callApiAsync(
      () => getServiceOrderById(serviceOrderId),
      (data) => {
        setEngineeringReportbyOrderId(data);
      },
      setLoadingDetails
    );
  };

  const selectsServiceOrder = async () => {
    await callApiAsync(
      () => getSelectsServiceOrder(),
      (data) => {
        setBrandData(data.brandList);
        setEquipmentData(data.equipmentList);
      },
      setLoadingDetails
    );
  };

  const handleSaveAll = async (state) => {
    const EnginnerReportCreationDate = getValues("creationDate") || null;
    const EnginnerReportCloseDate = getValues("closeDate") || null;
    const preventiveMaintenanceDate =
      getValues("preventive_maintenance_performed") || null;
    const nextMaintenanceDate = getValues("nextMaintenance") || null;
    const selectedServices = getValues("services") || [];

    const reportbyInstitutionValue = getValues("reportbyInstitution");
    const engineeringReportValue = getValues("engineeringReport");
    const observationsValue = getValues("observations");

    const selectedValue = watch("services", []);

    const isSurveyFilled = (activities) => {
      return activities.some((activity) => activity.value !== null);
    };

    const getSurveyStatus = (activities) => {
      if (activities.every((a) => a.value === null)) return "empty";
      if (activities.every((a) => a.value !== null)) return "complete";
      return "partial";
    };

    const activityDataValidation = getSurveyStatus(activityData);
    if (state === engineerReportState.complete) {
      if (ImageCount < 2) {
        setToastConfig({
          severity: "error",
          summary: "Firma requerida",
          detail: "Por favor, sube al menos 2 imágenes de evidencia."
        });
        setShowToast(true);
        return;
      }

      if (!nextMaintenanceDate || !selectedServices) {
        setToastConfig({
          severity: "error",
          summary: "Campos obligatorios incompletos",
          detail:
            "Por favor, complete las fechas de próximo mantenimiento: y mantenimiento preventivo ejectutado."
        });
        setShowToast(true);
        return;
      }

      if (!EnginnerReportCreationDate || !EnginnerReportCloseDate) {
        setToastConfig({
          severity: "error",
          summary: "Campos obligatorios incompletos",
          detail:
            "Por favor, complete las fechas de creación y cierre del servicio."
        });
        setShowToast(true);
        return;
      }

      if (activityDataValidation !== "complete" || activityData.length < 1) {
        setToastConfig({
          severity: "error",
          summary: "Debe indicar el estado de una actividad",
          detail:
            "Por favor, alguna actividad de la tabla no debe estar completada."
        });
        setShowToast(true);
        return;
      }

      if (
        !reportbyInstitutionValue ||
        !engineeringReportValue ||
        !observationsValue
      ) {
        setToastConfig({
          severity: "error",
          summary: "Campos obligatorios incompletos",
          detail:
            "Por favor, complete los campos de reporte por institución, informe de ingeniería y observaciones."
        });
        setShowToast(true);
        return;
      }

      if (!selectedValue || selectedValue.length < 1) {
        setToastConfig({
          severity: "error",
          summary: "Firma requerida",
          detail:
            "Por favor, asegúrese de que seleccionaste un tipo de Servicio."
        });
        setShowToast(true);
        return;
      }
    }

    const dataToSend = {
      requestId: Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (value) => value.toString(16).padStart(2, "0")).join(""),
      userId: userData.id,
      id: engineeringReportId ? engineeringReportId : 0,
      state: state,
      institutionId: engineeringReport
        ? engineeringReport.institution_id
        : engineeringReportbyOrderId.institutionId,
      serviceCreationDate:
        EnginnerReportCreationDate != null
          ? formatDateOnly(EnginnerReportCreationDate)
          : null,
      serviceClosureDate:
        EnginnerReportCloseDate != null
          ? formatDateOnly(EnginnerReportCloseDate)
          : null,
      serviceOrderId: serviceOrderId,
      preventiveMaintenanceDate:
        preventiveMaintenanceDate != null
          ? formatDateOnly(preventiveMaintenanceDate)
          : null,
      nextMaintenanceDate:
        nextMaintenanceDate != null
          ? formatDateOnly(nextMaintenanceDate)
          : null,
      isPreventiveMaintenance: selectedServices.includes(
        engineerReportTypeService.PreventiveMaintenance
      )
        ? 1
        : 0,
      isCorrectiveMaintenance: selectedServices.includes(
        engineerReportTypeService.CorrectiveMaintenance
      )
        ? 1
        : 0,
      isConsumableAdjustments: selectedServices.includes(
        engineerReportTypeService.ConsumablesAdjustment
      )
        ? 1
        : 0,
      isAcademicActivity: selectedServices.includes(
        engineerReportTypeService.AcademicActivity
      )
        ? 1
        : 0,
      isPreinstallation: selectedServices.includes(
        engineerReportTypeService.PreInstallation
      )
        ? 1
        : 0,
      isInstallation: selectedServices.includes(
        engineerReportTypeService.Installation
      )
        ? 1
        : 0,
      isSoftwareUpdate: selectedServices.includes(
        engineerReportTypeService.SoftwareUpdate
      )
        ? 1
        : 0,
      isTraining: selectedServices.includes(engineerReportTypeService.Training)
        ? 1
        : 0,
      isOther: selectedServices.includes(engineerReportTypeService.Other)
        ? 1
        : 0,
      isOtherValue: selectedServices.includes(engineerReportTypeService.Other)
        ? getValues("otherOp")
        : "" || "",

      reportedNovelty: reportbyInstitutionValue,
      report: engineeringReportValue,
      hasSpareParts: hasSpareParts,
      observations: observationsValue,

      madeByUserId: engineeringReport
        ? engineeringReport.made_by_user_id
        : engineeringReportbyOrderId.engineer_id,
      madeBySign: engineeringReport
        ? engineeringReport.made_by_sign
        : engineeringReportbyOrderId.engineer_signature,

      receivedByName: getValues("receivedByName"),
      receivedByPosition: getValues("receivedByPosition"),
      receivedBySign: state === engineerReportState.complete ? signature : null,

      approver_signature_id: userData.id
    };

    await callActionApiAsync(
      () => saveEngineeringAct(dataToSend),
      (data) => {
        reset(getValues());
        setSignatureChanged(false);
        if (state === engineerReportState.partialSave) setSignature(null);
        setOpenPartialSignatureWarning(false);
        dispatch(setEngineeringReportId(data.id));
        setOpenVerification(false);
        getEngineeringActData();
        setSavingReport(false);
        setNavigationDirty(pendingEvidences || pendingActivity || pendingSparePart);
        protectedActionRef.current = "navigating";
        navigate("/biomedicalManagementDashboard/serviceRecords");
      },
      (error) => {
        console.error("[Reporte Ingeniería] Error al guardar", { error });
        setToastConfig({
          severity: "error",
          summary: "Error al guardar",
          detail:
            typeof error === "string"
              ? error
              : error?.message || "Revisa la consola para más detalles."
        });
        setShowToast(true);
      }
    );
  };

  const isComplete = engineeringReport
    ? engineeringReport.state === engineerReportState.complete
    : false;
  const hasClientSignature = !!engineeringReport?.received_by_sign;
  const isSavedWithoutClientSignature = engineeringReport
    ? engineeringReport.state === engineerReportState.complete &&
      !engineeringReport.received_by_sign
    : false;
  const canEditUnsignedReport = canEditEngineeringReport;
  const isUnsignedReportEditable =
    isSavedWithoutClientSignature &&
    canEditUnsignedReport &&
    isEditingUnsignedReport;
  const isReadOnly = hasClientSignature || (isComplete && !isUnsignedReportEditable);
  const canEnableUnsignedEdit =
    isSavedWithoutClientSignature &&
    canEditUnsignedReport &&
    !isEditingUnsignedReport;

  const setNavigationDirty = useUnsavedReportChanges(
    pendingEvidences || pendingActivity || pendingSparePart ||
    (!isReadOnly && (Object.keys(dirtyFields).length > 0 || signatureChanged || Boolean(hasSpareParts) !== Boolean(
      (engineeringReport ?? engineeringReportbyOrderId)?.has_spare_parts)))
  );

  const handleSignatureChange = (value) => {
    setSignature(value);
    setSignatureChanged(true);
  };

  const handlePartialSave = () => {
    if (signature) {
      setOpenPartialSignatureWarning(true);
      return;
    }
    return runReportSave(engineerReportState.partialSave);
  };

  const handleImageCountChange = (count) => {
    setImageCount(count);
  };

  useEffect(() => {
    // Release after the enabled edit state is rendered, preventing stale clicks.
    if (isEditingUnsignedReport && protectedActionRef.current === "enableUnsignedEdit") {
      protectedActionRef.current = null;
      setActiveAction(null);
    }
  }, [isEditingUnsignedReport]);

  useEffect(() => {
    if (engineeringReportId !== 0) {
      setIsEditingUnsignedReport(false);
      setOpenEnableEditVerification(false);
      getEngineeringActData();
    } else if (serviceOrderId) {
      getServiceOrderData();
    }
  }, [serviceOrderId, engineeringReportId]);

  useEffect(() => {
    getNonBusinessDaysData();
    selectsServiceOrder();
  }, []);

  useEffect(() => {
    loadData();
  }, [engineeringReport]);

  useEffect(() => {
    setIsNew(isNewReport || false);
  }, [isNewReport]);

  return (
    <div>
      <div inert={activeAction ? "" : undefined}>
        <BreadCrumbComp />
      </div>
      <div className="searchTitle">
        <h1>CREAR NUEVO REPORTE DE INGENIERÍA</h1>
      </div>
      <HeaderBanner type={headerBannerType.createEngineeringReport} />
      <div className="create-form-container">
        <LoadingFeedback loading={loadingDetails} rows={8}>
        <fieldset disabled={!!activeAction} inert={activeAction ? "" : undefined}
          style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <p className="createRist-title ml-0">
          REPORTE DE INGENIERÍA - {serviceOrder.serviceOrderName}
        </p>
        <p className="createRist-title ml-0 mt-25">1. Datos del Cliente</p>
        <Grid container justifyContent="center" spacing={2}>
          <Grid item xs={12} md={6}>
            <p className="create-label ml-0">Fecha de creación del servicio:</p>
            <FormCalendar
              name="creationDate"
              control={control}
              label=""
              defaultValue={null}
              formLabel=""
              error={errors.deliveryDate}
              margin={"0"}
              disabledDates={disabledDates}
              minDate={minDate}
              maxDate={maxDate}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <p className="create-label ml-0">Fecha del cierre del servicio:</p>
            <FormCalendar
              name="closeDate"
              control={control}
              label=""
              defaultValue={null}
              formLabel=""
              error={errors.deliveryDate}
              margin={"0"}
              disabledDates={disabledDates}
              minDate={minDate}
              maxDate={maxDate}
              disabled={isReadOnly}
            />
          </Grid>
        </Grid>
        <InstitutionSection
          control={control}
          setValue={setValue}
          errors={errors}
          data={engineeringReport ?? engineeringReportbyOrderId}
        />
        <p className="createRist-title ml-0 mt-25">2. Tecnología Médica</p>
        <MedicalTechnologySection
          control={control}
          setValue={setValue}
          errors={errors}
          getValues={getValues}
          watch={watch}
          serviceOrder={serviceOrder}
          brandData={brandData}
          equipmentData={equipmentData}
          disabledDates={disabledDates}
          data={engineeringReport ?? engineeringReportbyOrderId}
        />
        <p className="createRist-title ml-0 mt-25">3. Tipo de Servicio</p>
        <ServiceTypeSection
          control={control}
          setValue={setValue}
          errors={errors}
          getValues={getValues}
          watch={watch}
          engineeringReport={engineeringReport}
          isComplete={isReadOnly}
          isNew={isNewReport}
        />
        <p className="createRist-title ml-0 mt-25 mb-10">
          4. Novedad reportada por la Institución
        </p>
        <div>
          <div>
            <CharacterCounter
              name={"reportbyInstitution"}
              control={control}
              maxLength={500}
            />
          </div>
          {/* <div> */}
          <FormTextField
            name={"reportbyInstitution"}
            control={control}
            isTextArea={true}
            error={errors.reportbyInstitution}
            maxLength={500}
            rows={3}
            margin={"0"}
            disabled={isReadOnly}
          />
        </div>
        <p className="createRist-title ml-0 mt-25">5. Actividad</p>

        <ActivityEnginnerSection
          userId={userData.id}
          isPageBusy={!!activeAction}
          startProtectedAction={startProtectedAction}
          endProtectedAction={endProtectedAction}
          onPendingChangesChange={setPendingActivity}
          serviceOrderId={serviceOrderId}
          engineeringActId={engineeringReportId}
          isComplete={isReadOnly || (Boolean(engineeringReportId) && !engineeringReport)}
          dataER={engineeringReport ?? engineeringReportbyOrderId}
          setActivityDataList={setActivityData}
        />

        <p className="createRist-title ml-0 mt-25">6. Reporte de Ingeniería</p>
        <div>
          <div>
            <CharacterCounter
              name={"engineeringReport"}
              control={control}
              maxLength={1000}
            />
          </div>
          <FormTextField
            name={"engineeringReport"}
            control={control}
            isTextArea={true}
            error={errors.engineeringReport}
            maxLength={1000}
            rows={3}
            margin={"0"}
            disabled={isReadOnly}
          />
        </div>
        <p className="createRist-title ml-0 mt-25">Repuestos utilizados</p>
        <SparePartsUsedSection
          onPendingChangesChange={setPendingSparePart}
          onSparePartsUsedChange={setHasSpareParts}
          dataER={engineeringReport ?? engineeringReportbyOrderId}
          isComplete={isReadOnly}
          serviceOrderId={serviceOrderId}
        />

        <p className="createRist-title ml-0 mt-25">
          7. Observaciones y/o recomendaciones
        </p>
        <div>
          <div>
            <CharacterCounter
              name={"observations"}
              control={control}
              maxLength={500}
            />
          </div>
          {/* <div> */}
          <FormTextField
            name={"observations"}
            control={control}
            isTextArea={true}
            error={errors.engineeringReport}
            maxLength={500}
            rows={3}
            margin={"0"}
            disabled={isReadOnly}
          />
        </div>
        <p className="createRist-title ml-0 mt-25">
          8. Anexos/ Evidencias Fotográficas
        </p>
        <EvidencesSection
          userId={userData.id}
          isPageBusy={!!activeAction}
          startProtectedAction={startProtectedAction}
          endProtectedAction={endProtectedAction}
          onPendingChangesChange={setPendingEvidences}
          engineeringReportId={engineeringReportId}
          serviceOrderId={serviceOrderId}
          isComplete={isReadOnly}
          dataER={engineeringReport ?? engineeringReportbyOrderId}
          onImageCountChange={handleImageCountChange}
        />
        <Divider className="mv-25" />
        <SignSection
          onSignatureChange={handleSignatureChange}
          data={engineeringReport ?? engineeringReportbyOrderId}
          setSignature={setSignature}
          signature={signature}
          isComplete={isReadOnly}
          control={control}
          errors={errors}
          setValue={setValue}
          getValues={getValues}
        />

        {canEnableUnsignedEdit && (
          <div
            className="j-center mt-20"
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              marginTop: 24
            }}
          >
            <LaddaButton
              type="button"
              variant="outlined"
              disabled={!!activeAction}
              loading={activeAction === "enableUnsignedEdit"}
              onClick={openUnsignedEditConfirmation}
            >
              Editar reporte sin firma
            </LaddaButton>
          </div>
        )}

        {hasClientSignature && (
          <Alert
            severity="info"
            sx={{ mt: 3, borderRadius: 2, alignItems: "center" }}
          >
            Este reporte no es posible editarlo porque ya fue firmado por el
            cliente y recibido.
          </Alert>
        )}

        {!isReadOnly && (
          <div
            className="j-center mt-20"
            style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: 24 }}
          >
            <LaddaButton
              type="button"
              variant="contained"
              disabled={!!activeAction}
              loading={savingReport && activeAction === "partial"}
              onClick={handlePartialSave}
            >
              Guardado parcial
            </LaddaButton>
            <LaddaButton
              type="button"
              variant="outlined"
              disabled={!!activeAction}
              loading={savingReport && activeAction === "report"}
              onClick={openReportConfirmation}
            >
              Guardar Reporte de Ingeniería
            </LaddaButton>
          </div>
        )}
        </fieldset>
        </LoadingFeedback>
      </div>

      <ToastNotification
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
        showToast={showToast}
        setShowToast={setShowToast}
      />

      <VerificationModal
        open={openPartialSignatureWarning}
        handleClose={() => setOpenPartialSignatureWarning(false)}
        iconSeverity="warning"
        loading={savingReport}
        alertText="La firma solo se guarda con Guardar Reporte de Ingenieria. Si continuas con el guardado parcial, la firma no se guardara y el reporte quedara sin firma. ¿Deseas continuar?"
        onConfirm={() => runReportSave(engineerReportState.partialSave)}
      />
      <VerificationModal
        open={openVerification}
        handleClose={closeReportConfirmation}
        loading={savingReport}
        alertText={`${
          !signature
            ? "¿Estás seguro de guardar este reporte de ingenieria sin firma del cliente? Podrás habilitar su edición nuevamente para actualizar la información o firmarlo más adelante."
            : "¿Estás seguro de guardar este reporte de ingenieria?"
        }`}
        onConfirm={() => runReportSave(engineerReportState.complete)}
      />
      <VerificationModal
        open={openEnableEditVerification}
        handleClose={closeUnsignedEditConfirmation}
        loading={activeAction === "enableUnsignedEdit"}
        alertText={
          "¿Seguro que quieres activar la edición de este reporte sin firma? Esto actualizará el PDF y la información que modifiques cuando vuelvas a guardar el reporte."
        }
        onConfirm={enableUnsignedEdit}
      />
    </div>
  );
};

export default CreateEngineeringReport;
